/* ============================================================
   INFERMONDS — reveal + atmosphere + subscription
   Opens on black with the card, the sky lighting up behind the words; then
   the mark arrives already bright, swells until it takes the screen,
   collapses, and beams up into its slot with the page following it out of
   the dark. Once landed the mark is completely
   still. Scrolling carries it away and brings it back small in the header,
   where it doubles as the home button. The only other motion is the sky.
   ============================================================ */
(() => {
  "use strict";

  const LOGO = window.INFERMONDS_LOGO;
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const DPR = Math.min(window.devicePixelRatio || 1, 2);

  const starCanvas = document.getElementById("starfield");
  const markCanvas = document.getElementById("celestial");
  const sf = starCanvas.getContext("2d");
  const cx = markCanvas.getContext("2d");

  const markSlot = document.getElementById("markSlot");
  const wordmark = document.getElementById("wordmark");
  const flashEl = document.getElementById("flash");
  const overture = document.getElementById("overture");
  const ovLine1 = document.getElementById("ovLine1");
  const ovLine2 = document.getElementById("ovLine2");

  /* ---------------- language ----------------
     Strings live in js/i18n.js. The visitor's language is picked on load and
     applied before anything reads a string; the control in the coda is a
     fallback. Anything the form writes at runtime reads through say(), so it
     follows a switch too. */
  const I18N = window.INFERMONDS_I18N;
  let lang = I18N.detect();
  const say = key => I18N.t(lang, key);
  I18N.apply(lang);

  let W = 0, H = 0, B = 0;

  const clamp01 = v => (v < 0 ? 0 : v > 1 ? 1 : v);
  const lerp = (a, b, t) => a + (b - a) * t;
  const smooth = t => t * t * (3 - 2 * t);
  const easeOutCubic = t => 1 - Math.pow(1 - t, 3);
  const easeOutQuint = t => 1 - Math.pow(1 - t, 5);
  const easeInExpo = t => (t <= 0 ? 0 : Math.pow(2, 10 * (t - 1)));
  // slow to leave, quick through the middle, soft landing — reads as "shot up"
  const easeInOutQuart = t =>
    t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2;
  const ph = (t, a, b) => clamp01((t - a) / (b - a));

  /* ---------------- intro timeline ----------------
     The card opens on black. The sky fills from the middle outward behind
     the words. The mark then arrives mid-flash — never seen still first —
     swells to take the screen, collapses, and beams up into its slot with
     the rest of the page following it out of the dark. */
  const T = {
    ovIn1:   0.4,          // first line of the card
    ovIn2:   0.85,         // second, just behind it
    ovOut:   3.2,
    ovGone:  4.4,
    fieldIn: [2.2, 4.9],   // the sky lights while the words are still up
    /* Flash timings lifted frame-by-frame from the client's reference clip:
       a 1.0s rise, a brief hold at peak, then a 1.15s collapse. The beam-up
       starts during the collapse's tail so the two read as one movement.

       There is deliberately no separate "form" phase — the client did not
       want to see the logo sitting still before the flash, so the mark
       arrives already bright at `flare[0]` and starts growing immediately. */
    flare:    [4.6, 5.6],
    flareEnd: 6.75,
    shoot:    [6.3, 7.45],
  };

  /* The sequence no longer ends on a form. The wordmark is the last thing
     the intro says; a beat later the store takes over. */
  const REVEALS = [
    ["wordmark", 7.25],
  ];

  /* Long enough for the wordmark to land and be read, short enough that the
     store is never being waited for — about 1.3s after the brand moment. */
  const HANDOVER = 8.55;

  /* Returning within the session goes straight in. The full sequence is for
     arriving at INFERMONDS, not for every navigation inside it. */
  const SEEN_KEY = "infermonds.intro.seen";

  /* ── Does a returning visitor sit through the opening again? ──────────
     Currently ONCE PER SESSION: the opening plays on arrival, then a
     returning visitor goes straight in until they close the tab.

     Set to false to play it on every single load. It is a brand decision rather than a
     technical one — the opening is the strongest thing the site does, and
     how often a visitor should see it is a judgement about how precious to
     keep it. Left off for now so it can actually be watched.

     ?intro  always plays it   ?nointro  always skips it
     — both override this, so either behaviour can be checked without
     touching the code or clearing storage by hand. */
  const SKIP_ON_RETURN = true;

  const forceIntro = /[?&]intro\b/.test(location.search);
  const forceSkip  = /[?&]nointro\b/.test(location.search);

  /* Someone opening /privacy, /terms or a product link asked for that page,
     not for the opening. Making them sit through a cinematic before they can
     read a privacy notice is hostile, and for a legal page it is the kind of
     thing that gets held against you — the information has to be reachable.
     The opening belongs to the front door only. */
  const deepLink = location.pathname !== "/" && location.pathname !== "";

  const seenThisSession = (() => {
    if (forceIntro) return false;
    if (forceSkip) return true;
    if (deepLink) return true;
    if (!SKIP_ON_RETURN) return false;
    try { return sessionStorage.getItem(SEEN_KEY) === "1"; } catch { return false; }
  })();
  const markSeen = () => { try { sessionStorage.setItem(SEEN_KEY, "1"); } catch {} };

  /* ---------------- sprites ---------------- */

  function radial(size, stops) {
    const c = document.createElement("canvas");
    c.width = c.height = size;
    const g = c.getContext("2d");
    const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    for (const [o, col] of stops) grad.addColorStop(o, col);
    g.fillStyle = grad;
    g.fillRect(0, 0, size, size);
    return c;
  }

  const spGlow = radial(512, [
    [0, "rgba(255,255,255,0.44)"],
    [0.16, "rgba(228,234,255,0.19)"],
    [0.44, "rgba(210,218,248,0.06)"],
    [1, "rgba(204,214,244,0)"],
  ]);

  // the outermost bloom — broad and faint, it gives the mark its reach
  const spHalo = radial(512, [
    [0, "rgba(255,255,255,0.2)"],
    [0.2, "rgba(236,240,255,0.1)"],
    [0.52, "rgba(214,222,250,0.032)"],
    [1, "rgba(206,216,246,0)"],
  ]);

  /* The magenta halo. Sits behind and slightly wider than the white bloom,
     so the light fringes pink at its edges the way an intensely blown-out
     highlight does through real glass. Kept low-alpha: it should read as
     lens character on a monochrome brand, not as a colour. */
  /* A copy of the mark tinted magenta and then masked so only its outer
     extremities survive — the arm tips and the flare needles. Drawn a touch
     larger than the white mark, it leaves a faint pink edge on the points
     and nothing anywhere else. Additive light can't tint an already-white
     pixel, so the colour has to sit just *past* the white to be seen. */
  function tipTint(size, color, blur) {
    const c = LOGO.sprite(size, { blur, color });
    const g = c.getContext("2d");
    g.globalCompositeOperation = "destination-in";
    const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    grad.addColorStop(0.00, "rgba(0,0,0,0)");
    grad.addColorStop(0.58, "rgba(0,0,0,0)");   // the whole body stays white
    grad.addColorStop(0.82, "rgba(0,0,0,0.6)");
    grad.addColorStop(1.00, "rgba(0,0,0,1)");
    g.fillStyle = grad;
    g.fillRect(0, 0, size, size);
    return c;
  }

  const markTips = tipTint(640, "#ff5ecb", 2.5);
  const markTipsSoft = tipTint(640, "#ff7ad8", 9);
  const markBloom = LOGO.sprite(640, { blur: 26, gain: 0.9 });
  const markMid = LOGO.sprite(640, { blur: 5, gain: 0.85 });
  const markCore = LOGO.sprite(640, { blur: 0.6 });

  /* The light thrown ahead of the approaching mark. Nearly solid in the
     middle with a short falloff, so as it scales up it reads as a mass of
     light closing on you rather than a soft vignette. */
  const spRush = radial(512, [
    [0, "rgba(255,255,255,1)"],
    [0.45, "rgba(255,253,255,0.92)"],
    [0.72, "rgba(250,244,255,0.42)"],
    [0.9, "rgba(246,238,255,0.1)"],
    [1, "rgba(244,236,255,0)"],
  ]);

  const spDot = radial(32, [
    [0, "rgba(255,255,255,1)"],
    [0.28, "rgba(250,252,255,0.85)"],
    [0.55, "rgba(232,238,255,0.25)"],
    [1, "rgba(226,234,255,0)"],
  ]);

  const spGlimmer = (() => {
    const size = 96, h = size / 2;
    const c = document.createElement("canvas");
    c.width = c.height = size;
    const g = c.getContext("2d");
    g.translate(h, h);
    const grad = g.createRadialGradient(0, 0, 0, 0, 0, h);
    grad.addColorStop(0, "rgba(255,255,255,1)");
    grad.addColorStop(0.12, "rgba(255,255,255,0.55)");
    grad.addColorStop(0.35, "rgba(226,234,255,0.1)");
    grad.addColorStop(1, "rgba(220,230,255,0)");
    g.fillStyle = grad;
    g.fillRect(-h, -h, size, size);
    g.filter = "blur(0.7px)";
    g.fillStyle = "rgba(255,255,255,0.85)";
    for (let i = 0; i < 2; i++) {
      g.save();
      g.rotate((i * Math.PI) / 2);
      g.beginPath();
      g.moveTo(-h + 4, 0);
      g.lineTo(0, -1.4);
      g.lineTo(h - 4, 0);
      g.lineTo(0, 1.4);
      g.closePath();
      g.fill();
      g.restore();
    }
    return c;
  })();

  /* ---------------- starfield ---------------- */

  let stars = [], dust = [], comets = [], nextComet = 2;

  /* Each star gets its own ignition moment, ordered by distance from the
     middle of the sky, so the field lights outward instead of the whole
     layer fading up at once. */
  function birthOf(x, y) {
    const dx = (x - W / 2) / (W / 2);
    const dy = (y - H * 0.5) / (H / 2);
    const d = Math.min(1, Math.hypot(dx, dy) / 1.414);
    const span = T.fieldIn[1] - T.fieldIn[0];
    return T.fieldIn[0] + span * (0.72 * d + 0.28 * Math.random());
  }

  function buildField() {
    const count = Math.round((W * H) / 5200) + 90;
    stars = [];
    for (let i = 0; i < count; i++) {
      const sx = Math.random() * W, sy = Math.random() * H;
      stars.push({
        x: sx,
        y: sy,
        born: reduceMotion ? -1 : birthOf(sx, sy),
        r: 0.3 + Math.pow(Math.random(), 2.6) * 1.25,
        a: 0.1 + Math.pow(Math.random(), 1.9) * 0.5,
        vx: (Math.random() - 0.5) * 0.7,
        vy: (Math.random() - 0.5) * 0.7,
        depth: 0.35 + Math.random() * 0.65,
        glimmers: Math.random() < 0.45,
        next: Math.random() * 5,
        dur: 0.9 + Math.random() * 1.5,
        peak: 0.72 + Math.random() * 0.38,
        t0: -99,
      });
    }
    for (let i = 0; i < Math.max(6, Math.round((W * H) / 150000)); i++) {
      const sx = Math.random() * W, sy = Math.random() * H;
      stars.push({
        x: sx,
        y: sy,
        born: reduceMotion ? -1 : birthOf(sx, sy),
        r: 1.5 + Math.random() * 1.2,
        a: 0.45 + Math.random() * 0.35,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        depth: 1,
        glimmers: true,
        anchor: true,
        next: Math.random() * 4,
        dur: 1.4 + Math.random() * 1.6,
        peak: 0.9 + Math.random() * 0.3,
        t0: -99,
      });
    }

    dust = [];
    for (let i = 0; i < 24; i++) {
      dust.push({
        x: (0.5 + (Math.random() - 0.5) * 0.55) * W,
        y: (0.45 + (Math.random() - 0.5) * 0.55) * H,
        r: 0.5 + Math.random() * 0.9,
        a: 0.03 + Math.random() * 0.055,
        vx: (Math.random() - 0.5) * 2,
        vy: -0.9 - Math.random() * 2,
      });
    }
    comets = [];
  }

  /* ---------------- shooting stars ----------------
     One at a time, rarely, on a shallow diagonal. Each is a bright head
     dragging a tapered tail; both fade in and out across the flight so
     nothing pops. Deliberately sparse — a streak every few seconds reads
     as deep space, a constant stream reads as a screensaver. */

  function spawnComet(t) {
    const dir = Math.random() < 0.5 ? 1 : -1;
    const angle = (Math.random() * 12 + 16) * (Math.PI / 180);
    const speed = (W + H) * (0.22 + Math.random() * 0.16);
    comets.push({
      x: dir > 0 ? -0.12 * W : W * 1.12,
      y: Math.random() * H * 0.62,
      born: t,
      life: 1.0 + Math.random() * 0.7,
      vx: Math.cos(angle) * speed * dir,
      vy: Math.sin(angle) * speed,
      len: 90 + Math.random() * 150,
      thick: 1.1 + Math.random() * 0.9,
      bright: 0.68 + Math.random() * 0.32,
    });
  }

  function drawComets(t, dt) {
    if (t > nextComet) {
      if (comets.length < 3) spawnComet(t);
      nextComet = t + 1.8 + Math.random() * 3.6;
    }

    for (let i = comets.length - 1; i >= 0; i--) {
      const c = comets[i];
      const age = t - c.born;
      if (age > c.life) { comets.splice(i, 1); continue; }

      c.x += c.vx * dt;
      c.y += c.vy * dt;
      if (c.y > H + 200 || c.x < -0.3 * W || c.x > W * 1.3) { comets.splice(i, 1); continue; }

      const a = Math.sin(Math.PI * (age / c.life)) * c.bright;
      if (a <= 0.01) continue;

      const n = Math.hypot(c.vx, c.vy) || 1;
      const tx = (c.vx / n) * c.len;
      const ty = (c.vy / n) * c.len;

      const grad = sf.createLinearGradient(c.x, c.y, c.x - tx, c.y - ty);
      grad.addColorStop(0, `rgba(255,255,255,${Math.min(1, a)})`);
      grad.addColorStop(0.35, `rgba(238,244,255,${Math.min(1, a * 0.35)})`);
      grad.addColorStop(1, "rgba(226,236,255,0)");

      sf.globalAlpha = 1;
      sf.strokeStyle = grad;
      sf.lineWidth = c.thick;
      sf.lineCap = "round";
      sf.beginPath();
      sf.moveTo(c.x, c.y);
      sf.lineTo(c.x - tx, c.y - ty);
      sf.stroke();

      // head: a soft point with a sharp glint sitting on top of it
      const hr = 5 + c.thick * 2.2;
      sf.globalAlpha = Math.min(1, a);
      sf.drawImage(spDot, c.x - hr, c.y - hr, hr * 2, hr * 2);
      const gr = hr * 2.6;
      sf.globalAlpha = Math.min(1, a * 0.9);
      sf.drawImage(spGlimmer, c.x - gr, c.y - gr, gr * 2, gr * 2);
    }
  }

  /* ---------------- where the mark sits ----------------
     Dead centre of its slot, which the flex layout centres in the viewport.
     (An earlier version anchored it to the gap between the R and the M of
     INFERMONDS; once the mark settled smaller that offset read as an error
     rather than as optical centring, so it is centred outright.) */

  function markCentre() {
    const r = markSlot.getBoundingClientRect();
    return {
      x: r.left + r.width / 2,
      y: r.top + r.height / 2,
      size: r.width,
    };
  }

  /* Where the mark is, and how bright, at time t.
     Before the shoot it hangs in the middle of the screen, larger; then it
     travels up into its slot, shrinking as it goes. */
  function markState(t) {
    const home = markCentre();
    if (reduceMotion) {
      return { x: home.x, y: home.y, size: home.size, alpha: 1, boost: 1, flash: 0, travel: 0 };
    }

    const stageX = W / 2;
    const stageY = H * 0.5;
    const stageSize = home.size * 1.5;

    /* Full brightness essentially on arrival. The 0.12s is only there to
       avoid a hard pop on the first frame — short enough that the mark reads
       as already lit when the flash starts, which is the point. */
    const appear = ph(t, T.flare[0], T.flare[0] + 0.12);

    /* The flash, matched to the reference clip's measured curve.

       Rise: pow(k, 0.6) — it jumps hard out of the gate (a fifth of the way
       up in the first tenth of the window) then climbs steadily. Fall: holds
       at peak for the first 15% of the window, then drops away with a long
       taper. Fitted: mean error 0.025 against the clip, max 0.08. Both were fitted against the frame-by-frame brightness of the
       clip; don't swap them for a generic ease, the character is in the
       fast start and the hold. */
    let flare;
    if (t < T.flare[1]) {
      flare = Math.pow(ph(t, T.flare[0], T.flare[1]), 0.6);
    } else {
      const c = ph(t, T.flare[1], T.flareEnd);
      flare = c < 0.15 ? 1 : Math.pow(1 - (c - 0.15) / 0.85, 2.1);
    }

    const p = ph(t, T.shoot[0], T.shoot[1]);
    const k = easeInOutQuart(p);

    /* The reference never blows the frame to pure white — it peaks around
       two thirds. Most of that light comes off the star itself, so the
       overlay only lifts the frame the rest of the way. */
    const flash = 0.34 * flare;

    return {
      x: lerp(stageX, home.x, k),
      y: lerp(stageY, home.y, k),
      // 5.4x radius at peak, measured off the clip's lit area
      size: lerp(stageSize * (1 + 4.4 * flare), home.size, easeOutQuint(p)),
      alpha: appear,
      boost: lerp(1 + 1.2 * flare, 1, easeOutCubic(p)),
      flash: Math.max(0, flash),
      // the star's own glow, swelling and collapsing with it
      rush: flare,
      // rises fast, holds through the climb, fades only on arrival
      travel: p > 0 && p < 1 ? Math.min(1, Math.sin(Math.PI * p) * 1.7) : 0,
      from: { x: stageX, y: stageY },
    };
  }

  /* ---------------- render ---------------- */

  let pointer = { tx: 0, ty: 0, x: 0, y: 0 };
  let pulse = 0;
  let start = null, pausedAt = null, lastNow = null, revealIdx = 0;

  /* Handover state. `fieldDim` takes the starfield down to punctuation once
     the store is up — the celestial language spends itself in the intro, and
     a full sky behind product photography would fight it. `markVis` fades the
     canvas-painted mark out as the header's own SVG mark takes its place. */
  const FIELD_REST = 0.16;      // starfield intensity once the store is up

  let handedOver = false;
  let fieldDim = 1;
  let markVis = 1;
  let ov1 = false, ov2 = false, ovOut = false, ovGone = false;

  function drawSprite(g, sprite, x, y, radius, alpha) {
    if (radius <= 0 || alpha <= 0) return;
    g.globalAlpha = alpha > 1 ? 1 : alpha;
    g.drawImage(sprite, x - radius, y - radius, radius * 2, radius * 2);
  }

  // radius of the mark relative to its slot; 0.8 gives the flare room to overhang
  const MARK_R = 0.62;   // the settled mark, a touch smaller
  const GLOW_R = 1.15;

  function paintMark(t, gain, st) {
    const a = st.alpha;
    if (a <= 0.001) return;

    /* Once it lands the mark is completely still — no breathing, no pointer
       drift. It is the fixed point of the page; only scrolling moves the
       world around it. `settled` also pulls the bloom back, because at full
       intro brightness the glow floods the silhouette and the crystalline
       detail inside it disappears. */
    const settled = reduceMotion ? 1 : ph(t, T.shoot[1] - 0.2, T.shoot[1] + 0.7);
    const dim = lerp(1, 0.4, settled);
    /* Everything soft is intro-only. At rest only the crisp core and a
       whisper of the mid layer survive, so the mark sits on clean black —
       the larger blurred copies were what read as a shadow behind it. */
    const haloDim = lerp(1, 0, settled);

    const g2 = gain * st.boost;
    const px = st.x;
    const py = st.y;
    const r = st.size * MARK_R * g2;
    const glow = st.size * GLOW_R * g2 * lerp(1, 0.72, settled);

    cx.save();
    cx.globalCompositeOperation = "lighter";
    cx.globalAlpha = 1;

    /* The beam. As the mark climbs it drags a column of light all the way
       back to where it launched: a hot narrow core inside two progressively
       wider, softer sheaths, each tapering to a point at the tail. The stack
       is what makes it read as a beam lifting the mark rather than a scratch
       across the sky. */
    if (st.travel > 0.01) {
      const dx = px - st.from.x, dy = py - st.from.y;
      const dist = Math.hypot(dx, dy);
      if (dist > 4) {
        const ux = dx / dist, uy = dy / dist;
        const len = dist;                       // right back to the launch point
        const tailX = px - ux * len, tailY = py - uy * len;
        const nx = -uy, ny = ux;

        for (const [widthMul, alphaMul] of [[9, 0.11], [3.4, 0.24], [1.2, 0.62]]) {
          const hw = Math.max(1.2, r * 0.05 * widthMul);
          const grad = cx.createLinearGradient(px, py, tailX, tailY);
          grad.addColorStop(0, `rgba(255,255,255,${(alphaMul * st.travel * a).toFixed(3)})`);
          grad.addColorStop(0.28, `rgba(255,242,253,${(alphaMul * 0.55 * st.travel * a).toFixed(3)})`);
          grad.addColorStop(0.7, `rgba(255,190,240,${(alphaMul * 0.18 * st.travel * a).toFixed(3)})`);
          grad.addColorStop(1, "rgba(255,150,225,0)");
          cx.fillStyle = grad;
          cx.beginPath();
          cx.moveTo(px + nx * hw, py + ny * hw);
          cx.lineTo(tailX, tailY);
          cx.lineTo(px - nx * hw, py - ny * hw);
          cx.closePath();
          cx.fill();
        }
      }
    }
    // a whisper of magenta just past the tips, laid down before the white
    drawSprite(cx, markTipsSoft, px, py, r * 1.12, 0.18 * a * dim);
    drawSprite(cx, markTips, px, py, r * 1.045, 0.2 * a * dim);

    // white from here — bloom scaled by `dim` so the settled mark reads as
    // a crisp silhouette rather than a blown-out blob
    drawSprite(cx, spHalo, px, py, glow * 2.3, 0.7 * a * haloDim);
    drawSprite(cx, spGlow, px, py, glow, 0.9 * a * haloDim);
    drawSprite(cx, spGlow, px, py, glow * 0.42, 0.65 * a * haloDim);
    drawSprite(cx, markBloom, px, py, r * 1.75, 0.3 * a * haloDim);
    drawSprite(cx, markBloom, px, py, r * 1.22, 0.6 * a * haloDim);
    drawSprite(cx, markMid, px, py, r, lerp(0.85, 0.16, settled) * a);
    drawSprite(cx, markCore, px, py, r, a);

    /* The star's own light, swelling with it. Scaled off `r` rather than the
       viewport so it grows and collapses in step with the mark, which is how
       the reference reads — the frame gets bright because the star does. */
    if (st.rush > 0.001) {
      drawSprite(cx, spRush, px, py, r * 1.55, st.rush);
    }
    cx.restore();
  }

  function render(now) {
    if (start === null) start = now;
    const t = (now - start) / 1000;
    const dt = Math.min(0.05, lastNow === null ? 0.016 : (now - lastNow) / 1000);
    lastNow = now;

    pointer.x += (pointer.tx - pointer.x) * 0.045;
    pointer.y += (pointer.ty - pointer.y) * 0.045;

    /* --- field --- */
    sf.clearRect(0, 0, W, H);
    for (const s of stars) {
      const age = t - s.born;
      if (age < 0) continue;
      // brightest as it arrives, then settling back to its resting value
      const ignite = easeOutCubic(clamp01(age / 0.85));
      const spark = 1 + 0.9 * Math.max(0, 1 - age / 0.7);

      s.x += s.vx * 0.016; s.y += s.vy * 0.016;
      if (s.x < -4) s.x += W + 8; else if (s.x > W + 4) s.x -= W + 8;
      if (s.y < -4) s.y += H + 8; else if (s.y > H + 4) s.y -= H + 8;

      let flare = 0;
      if (s.glimmers && age > 0.6) {
        if (t > s.next) { s.t0 = t; s.next = t + 1.4 + Math.random() * 5.5; }
        const e = (t - s.t0) / s.dur;
        if (e >= 0 && e <= 1) flare = Math.sin(Math.PI * e) * s.peak;
      }

      const px = s.x + pointer.x * 5 * s.depth;
      const py = s.y + pointer.y * 4 * s.depth;

      sf.globalAlpha = Math.min(1, s.a * spark) * ignite * fieldDim;
      const dr = s.r * 1.6 * (0.5 + 0.5 * ignite);
      sf.drawImage(spDot, px - dr, py - dr, dr * 2, dr * 2);

      if (flare > 0.01) {
        const gr = Math.min(s.anchor ? 26 : 15, (s.anchor ? 7 : 5.2) * s.r * (0.6 + flare));
        sf.globalAlpha = Math.min(1, flare * ignite) * fieldDim;
        sf.drawImage(spGlimmer, px - gr, py - gr, gr * 2, gr * 2);
      }
    }
    if (t > T.fieldIn[1]) drawComets(t, dt);
    sf.globalAlpha = 1;

    /* --- mark --- */
    cx.clearRect(0, 0, W, H);

    const gain = 1 + pulse;
    if (pulse > 0) pulse = Math.max(0, pulse - 0.016);

    cx.save();
    cx.globalCompositeOperation = "lighter";
    const c = markCentre();
    for (const d of dust) {
      d.x += d.vx * 0.016; d.y += d.vy * 0.016;
      if (d.y < -5 || d.x < -5 || d.x > W + 5) {
        d.x = (0.5 + (Math.random() - 0.5) * 0.55) * W;
        d.y = c.y + (0.15 + Math.random() * 0.4) * H * 0.5;
      }
      cx.globalAlpha = d.a;
      cx.fillStyle = "#e9eeff";
      cx.beginPath();
      cx.arc(d.x + pointer.x * 8, d.y + pointer.y * 6, d.r, 0, Math.PI * 2);
      cx.fill();
    }
    cx.restore();

    const st = markState(t);
    st.alpha *= markVis;
    paintMark(t, gain, st);
    flashEl.style.opacity = st.flash.toFixed(3);

    if (!ov1 && t >= T.ovIn1) { ovLine1.classList.add("is-in"); ov1 = true; }
    if (!ov2 && t >= T.ovIn2) { ovLine2.classList.add("is-in"); ov2 = true; }
    if (!ovOut && t >= T.ovOut) { overture.classList.add("is-out"); ovOut = true; }
    if (!ovGone && t >= T.ovGone) { overture.classList.add("is-gone"); ovGone = true; }

    while (revealIdx < REVEALS.length && t >= REVEALS[revealIdx][1]) {
      document.getElementById(REVEALS[revealIdx][0])?.classList.add("is-in");
      revealIdx++;
    }

    if (!handedOver && t >= HANDOVER) { handedOver = true; openStore(); }

    /* Both ramps ease toward their target rather than sliding linearly to
       it, so the sky settles and the mark dissolves instead of arriving on
       a ramp you can see. Framerate-independent: the approach is scaled by
       dt, so 60Hz and 120Hz land identically. */
    if (handedOver) {
      fieldDim += (FIELD_REST - fieldDim) * Math.min(1, dt * 1.45);

      /* Held until the mark has nearly finished its walk into the header,
         so it dissolves where it lands rather than on the way. */
      if (t >= HANDOVER + 0.7) {
        markVis += (0 - markVis) * Math.min(1, dt * 2.4);
        if (markVis < 0.015) markVis = 0;
      }
    }

    requestAnimationFrame(render);
  }

  /* ---------------- sizing ---------------- */

  function resize() {
    W = window.innerWidth;
    H = window.innerHeight;
    B = Math.min(W, H);
    for (const c of [starCanvas, markCanvas]) {
      c.width = Math.round(W * DPR);
      c.height = Math.round(H * DPR);
      c.style.width = W + "px";
      c.style.height = H + "px";
    }
    sf.setTransform(DPR, 0, 0, DPR, 0, 0);
    cx.setTransform(DPR, 0, 0, DPR, 0, 0);
    buildField();
    if (reduceMotion) paintStatic();
  }

  let resizeTimer;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(resize, 120);
  });


  resize();

  /* ---------------- pointer parallax ---------------- */

  if (!reduceMotion && window.matchMedia("(hover: hover)").matches) {
    window.addEventListener("pointermove", e => {
      pointer.tx = (e.clientX / W - 0.5) * 2;
      pointer.ty = (e.clientY / H - 0.5) * 2;
    }, { passive: true });
  }

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) pausedAt = performance.now();
    else if (pausedAt !== null && start !== null) {
      start += performance.now() - pausedAt;
      pausedAt = null;
      lastNow = null;   // don't let the gap register as one huge frame
    }
  });

  /* ---------------- handover to the store ----------------
     The seam the whole sequence is built around. Three things move at once
     so there is no moment where one page has ended and another has not yet
     begun:

       · the mark-slot walks from the stage into the header's brand lockup.
         The canvas reads this element's box every frame, so moving the box
         moves the glowing mark with it — no second animation to keep in sync.
       · the canvas mark fades out while the header's own SVG mark fades in,
         both occupying the same pixels, so the swap is invisible.
       · the store fades up and the starfield drops to ~16%.

     The wordmark is the only thing that actually leaves. */

  const intro = document.getElementById("intro");

  function openStore() {
    markSeen();

    const slot = document.getElementById("markSlot");
    const target = document.getElementById("brandMark");

    /* Measured rather than guessed: the header lockup is responsive, so the
       destination is wherever it has actually laid out at this width. */
    if (slot && target) {
      const a = slot.getBoundingClientRect();
      const b = target.getBoundingClientRect();
      if (a.width > 0 && b.width > 0) {
        slot.style.transformOrigin = "top left";
        slot.style.transition = reduceMotion
          ? "none"
          : "transform 1.3s cubic-bezier(0.22, 1, 0.36, 1)";
        slot.style.transform =
          `translate(${(b.left - a.left).toFixed(2)}px, ${(b.top - a.top).toFixed(2)}px) ` +
          `scale(${(b.width / a.width).toFixed(4)})`;
      }
    }

    intro?.classList.add("is-open");
    window.dispatchEvent(new CustomEvent("infermonds:opened"));
  }

  /* Skips straight to the opened state. Used by the returning-visitor path
     and by reduced motion, so both land on exactly the same DOM as the full
     sequence rather than a second, subtly different arrangement. */
  function openImmediately() {
    handedOver = true;
    fieldDim = FIELD_REST;
    markVis = 0;
    revealIdx = REVEALS.length;
    overture.classList.add("is-out", "is-gone");
    intro?.classList.add("is-open", "is-instant");
    document.body.classList.add("instant-open");
    markSeen();
    window.dispatchEvent(new CustomEvent("infermonds:opened"));
  }

  /* The skip button. It calls the same openImmediately() the returning
     visitor and reduced-motion paths use, so skipping lands on exactly the
     same DOM as sitting through it — not a third, subtly different state. */
  const skipBtn = document.getElementById("skipIntro");
  skipBtn?.addEventListener("click", () => {
    if (!handedOver) openImmediately();
  });
  window.addEventListener("infermonds:opened", () => {
    skipBtn?.classList.add("is-gone");
    /* Taken out of the tab order as well as hidden — a control that does
       nothing should not still be reachable by keyboard. */
    setTimeout(() => { if (skipBtn) skipBtn.hidden = true; }, 600);
  }, { once: true });

  /* The preview chip is hidden in CSS and revealed only here, so it cannot
     reach production by being forgotten. Default-off is the right way round:
     forgetting to add a class shows nothing, forgetting to remove one ships
     "checkout not connected" to every real visitor. */
  if (/^(localhost|127\.0\.0\.1|\[::1\])$/.test(location.hostname) ||
      location.hostname.endsWith(".localhost")) {
    document.body.classList.add("is-local");
  }

  /* Kept for review: replays the opening without a reload.
     Run INFERMONDS_REPLAY() in the console. */
  function replayIntro() {
    if (reduceMotion) return;
    const slot = document.getElementById("markSlot");
    if (slot) { slot.style.transition = "none"; slot.style.transform = ""; }
    intro?.classList.remove("is-open", "is-instant");
    document.body.classList.remove("store-open", "instant-open");
    for (const el of document.querySelectorAll(".reveal")) el.classList.remove("is-in");
    revealIdx = 0;
    handedOver = false;
    fieldDim = 1;
    markVis = 1;
    ov1 = ov2 = ovOut = ovGone = false;
    overture.classList.remove("is-out", "is-gone");
    ovLine1.classList.remove("is-in");
    ovLine2.classList.remove("is-in");
    comets.length = 0;
    nextComet = 2;
    for (const st of stars) st.t0 = -99;
    start = null;
    lastNow = null;
  }
  window.INFERMONDS_REPLAY = replayIntro;

  /* ---------------- footer mark + favicon ---------------- */

  (function brandMarks() {
    const d = LOGO.path;
    document.getElementById("footerMark").innerHTML =
      `<svg viewBox="0 0 100 100" aria-hidden="true">` +
      `<path d="${d}" fill="#f6f5f2" fill-rule="evenodd"/></svg>`;

    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">` +
      `<rect width="100" height="100" fill="#000"/>` +
      `<path d="${d}" fill="#fff" fill-rule="evenodd"/></svg>`;
    const link = document.getElementById("favicon") || document.createElement("link");
    link.rel = "icon";
    link.type = "image/svg+xml";
    link.href = "data:image/svg+xml," + encodeURIComponent(svg);
    if (!link.isConnected) document.head.appendChild(link);
  })();

  /* ---------------- start ---------------- */

  function paintStatic() {
    sf.clearRect(0, 0, W, H);
    for (const s of stars) {
      sf.globalAlpha = s.a;
      const dr = s.r * 1.6;
      sf.drawImage(spDot, s.x - dr, s.y - dr, dr * 2, dr * 2);
      if (s.anchor) {
        const gr = Math.min(20, 6 * s.r);
        sf.globalAlpha = 0.5;
        sf.drawImage(spGlimmer, s.x - gr, s.y - gr, gr * 2, gr * 2);
      }
    }
    sf.globalAlpha = 1;
    cx.clearRect(0, 0, W, H);
    paintMark(0, 1, markState(0));
  }

  if (reduceMotion) {
    /* No render loop here. The sky is painted once, dimmed to the same
       resting value the animated path settles on, and the store opens
       straight away. */
    fieldDim = FIELD_REST;
    markVis = 0;
    openImmediately();
    paintStatic();
    window.addEventListener("resize", paintStatic);
  } else if (seenThisSession) {
    /* Already been through the door this session: no overture, no flash.
       The field still animates — it is the brand's atmosphere, not a
       loading screen — it simply starts at its resting intensity. */
    openImmediately();
    requestAnimationFrame(render);
  } else {
    /* rAF does not run in a tab that is not compositing, so a page opened in
       a background tab has a frozen clock: without this the visitor would
       arrive part-way through the sequence whenever they finally looked at
       it. Restarting from zero on the first reveal means they see the
       opening from its beginning, which is the only version worth seeing. */
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "visible" || handedOver) return;
      start = null;
      lastNow = null;
    });
    requestAnimationFrame(render);
  }

  /* ============================================================
     subscription
     ============================================================ */

  /* ---------------- language picker ----------------
     Built here because it needs the form elements to exist before a change
     can be reflected. The strings themselves were applied much earlier. */

  (function buildLangPicker() {
    const sel = document.getElementById("langSelect");
    for (const [code, label] of I18N.LANGS) {
      const opt = document.createElement("option");
      opt.value = code;
      opt.textContent = label;
      sel.appendChild(opt);
    }
    sel.value = lang;
    sel.addEventListener("change", () => { lang = I18N.apply(sel.value); });
  })();

  /* Text lengths change with the language, so the wordmark gap has to be
     re-measured — and anything the form has already written needs redrawing
     in the new language. */
  document.addEventListener("i18n:changed", e => {
    // apply() dispatches before it returns, so take the language from the
    // event rather than waiting for the assignment at the call site —
    // otherwise everything redrawn here lags one switch behind.
    lang = e.detail.lang;
    if (reduceMotion) paintStatic();
    refreshFormText();
  });

  const COUNTRIES = [
    ["United Kingdom", "GB", "+44"], ["United States", "US", "+1"],
    ["France", "FR", "+33"], ["Italy", "IT", "+39"], ["Germany", "DE", "+49"],
    ["Spain", "ES", "+34"], ["Portugal", "PT", "+351"], ["Netherlands", "NL", "+31"],
    ["Belgium", "BE", "+32"], ["Ireland", "IE", "+353"], ["Switzerland", "CH", "+41"],
    ["Austria", "AT", "+43"], ["Sweden", "SE", "+46"], ["Norway", "NO", "+47"],
    ["Denmark", "DK", "+45"], ["Finland", "FI", "+358"], ["Poland", "PL", "+48"],
    ["Czechia", "CZ", "+420"], ["Greece", "GR", "+30"], ["Turkey", "TR", "+90"],
    ["Canada", "CA", "+1"], ["Mexico", "MX", "+52"], ["Brazil", "BR", "+55"],
    ["Argentina", "AR", "+54"], ["Chile", "CL", "+56"], ["Colombia", "CO", "+57"],
    ["Japan", "JP", "+81"], ["South Korea", "KR", "+82"], ["China", "CN", "+86"],
    ["Hong Kong", "HK", "+852"], ["Singapore", "SG", "+65"], ["Taiwan", "TW", "+886"],
    ["India", "IN", "+91"], ["Indonesia", "ID", "+62"], ["Thailand", "TH", "+66"],
    ["Vietnam", "VN", "+84"], ["Philippines", "PH", "+63"], ["Malaysia", "MY", "+60"],
    ["Australia", "AU", "+61"], ["New Zealand", "NZ", "+64"],
    ["United Arab Emirates", "AE", "+971"], ["Saudi Arabia", "SA", "+966"],
    ["Qatar", "QA", "+974"], ["Israel", "IL", "+972"],
    ["South Africa", "ZA", "+27"], ["Nigeria", "NG", "+234"], ["Ghana", "GH", "+233"],
    ["Kenya", "KE", "+254"], ["Egypt", "EG", "+20"], ["Morocco", "MA", "+212"],
  ];

  const flagOf = iso => String.fromCodePoint(...[...iso].map(c => 0x1f1e6 + c.charCodeAt(0) - 65));

  /* ── The signup module stops here on Shopify ──────────────────────────
     Everything below this line talked to /api/subscribe on the Railway
     server: the email/SMS toggle, the dial-code picker, the consent text,
     the honeypot, the retry. None of that exists on Shopify, where the
     footer form is Shopify's own {% form 'customer' %} — it posts itself
     and records marketing consent against the customer.

     Letting this module run there would be worse than useless: it binds a
     submit handler that calls preventDefault and POSTs to a server that is
     no longer there, so every signup would silently vanish.

     #modePhone is the sentinel because it exists only on the standalone
     page and never in the theme. That keeps ONE main.js serving both — the
     animation above is identical in each, which is the whole point. */
  if (!document.getElementById("modePhone")) return;

  const form = document.getElementById("subscribeForm");
  const modeEmail = document.getElementById("modeEmail");
  const modePhone = document.getElementById("modePhone");
  const fieldEmail = document.getElementById("fieldEmail");
  const fieldPhone = document.getElementById("fieldPhone");
  const emailInput = document.getElementById("emailInput");
  const phoneInput = document.getElementById("phoneInput");
  const country = document.getElementById("country");
  const countryBtn = document.getElementById("countryBtn");
  const countryList = document.getElementById("countryList");
  const countryFlag = document.getElementById("countryFlag");
  const countryDial = document.getElementById("countryDial");
  const errorEl = document.getElementById("fieldError");
  const submitBtn = document.getElementById("submitBtn");
  const submitText = document.getElementById("submitText");
  const statusEl = document.getElementById("formStatus");

  let mode = "email";
  let focusIdx = 0;
  let done = false;

  /* Same-origin: the endpoint is deployed with the site. Override on
     window before main.js loads if the API ever lives elsewhere. */
  const ENDPOINT = window.INFERMONDS_ENDPOINT || "/api/subscribe";
  const legalEl = document.getElementById("legal");
  const hpInput = document.getElementById("company");
  const formShownAt = Date.now();

  COUNTRIES.forEach(([name, iso, dial], i) => {
    const li = document.createElement("li");
    li.className = "country__item";
    li.setAttribute("role", "option");
    li.setAttribute("aria-selected", i === 0 ? "true" : "false");
    li.innerHTML =
      `<span class="flag">${flagOf(iso)}</span><span>${name}</span><span class="dial">${dial}</span>`;
    li.addEventListener("click", () => selectCountry(i));
    countryList.appendChild(li);
  });

  function selectCountry(i) {
    const [, iso, dial] = COUNTRIES[i];
    focusIdx = i;
    countryFlag.textContent = flagOf(iso);
    countryDial.textContent = dial;
    [...countryList.children].forEach((li, j) =>
      li.setAttribute("aria-selected", j === i ? "true" : "false"));
    closeCountry();
    phoneInput.focus();
  }

  function openCountry() {
    countryList.hidden = false;
    requestAnimationFrame(() => country.classList.add("is-open"));
    countryBtn.setAttribute("aria-expanded", "true");
    highlight(focusIdx, true);
    document.addEventListener("pointerdown", onOutside, true);
    document.addEventListener("keydown", onListKeys, true);
  }

  function closeCountry() {
    country.classList.remove("is-open");
    countryBtn.setAttribute("aria-expanded", "false");
    setTimeout(() => {
      if (!country.classList.contains("is-open")) countryList.hidden = true;
    }, 360);
    document.removeEventListener("pointerdown", onOutside, true);
    document.removeEventListener("keydown", onListKeys, true);
  }

  const onOutside = e => { if (!country.contains(e.target)) closeCountry(); };

  function highlight(i, scroll) {
    focusIdx = Math.max(0, Math.min(COUNTRIES.length - 1, i));
    [...countryList.children].forEach((li, j) => li.classList.toggle("is-focused", j === focusIdx));
    if (scroll) countryList.children[focusIdx].scrollIntoView({ block: "nearest" });
  }

  function onListKeys(e) {
    if (e.key === "Escape") { closeCountry(); countryBtn.focus(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); highlight(focusIdx + 1, true); }
    else if (e.key === "ArrowUp") { e.preventDefault(); highlight(focusIdx - 1, true); }
    else if (e.key === "Enter") { e.preventDefault(); selectCountry(focusIdx); }
    else if (/^[a-z]$/i.test(e.key)) {
      const i = COUNTRIES.findIndex(c => c[0].toLowerCase().startsWith(e.key.toLowerCase()));
      if (i >= 0) highlight(i, true);
    }
  }

  countryBtn.addEventListener("click", () =>
    country.classList.contains("is-open") ? closeCountry() : openCountry());

  function setMode(m) {
    mode = m;
    const isEmail = m === "email";
    modeEmail.classList.toggle("is-active", isEmail);
    modePhone.classList.toggle("is-active", !isEmail);
    modeEmail.setAttribute("aria-selected", String(isEmail));
    modePhone.setAttribute("aria-selected", String(!isEmail));
    fieldEmail.hidden = !isEmail;
    fieldPhone.hidden = isEmail;

    /* The consent paragraph is the evidence: it is posted verbatim with the
       signup and stored. So it has to describe the channel actually being
       agreed to — SMS wording under the SMS tab, email wording under Email.
       One shared paragraph would file every email signup under a consent to
       receive text messages, which is not what anyone agreed to. */
    if (legalEl) {
      legalEl.dataset.i18n = isEmail ? "legalEmail" : "legalSms";
      legalEl.textContent = say(legalEl.dataset.i18n);
    }

    hideError();
  }
  modeEmail.addEventListener("click", () => setMode("email"));
  modePhone.addEventListener("click", () => setMode("phone"));

  const showError = m => { errorEl.textContent = m; errorEl.classList.add("is-visible"); };
  const hideError = () => errorEl.classList.remove("is-visible");

  for (const input of [emailInput, phoneInput]) {
    input.addEventListener("input", hideError);
    input.addEventListener("focus", () => {
      const settle = () =>
        input.scrollIntoView({ block: "center", behavior: reduceMotion ? "auto" : "smooth" });
      window.visualViewport?.addEventListener("resize", settle, { once: true });
      setTimeout(settle, 320);
    });
  }

  /* Redraw the pieces the form writes itself, in whatever language is now
     selected — without resurrecting an error the user has already cleared. */
  function refreshFormText() {
    submitText.textContent = done ? say("done") : say("submit");
    if (statusEl.textContent) statusEl.textContent = done ? say("statusDone") : say("statusSubmitting");
    if (errorEl.classList.contains("is-visible")) {
      errorEl.textContent = mode === "email" ? say("errEmail") : say("errPhone");
    }
  }

  const setBtnText = txt => {
    submitBtn.classList.add("is-swapping");
    setTimeout(() => {
      submitText.textContent = txt;
      submitBtn.classList.remove("is-swapping");
    }, 300);
  };

  form.addEventListener("submit", e => {
    e.preventDefault();
    if (done || submitBtn.disabled) return;

    if (mode === "email") {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailInput.value.trim())) {
        showError(say("errEmail"));
        emailInput.focus();
        return;
      }
    } else {
      const digits = phoneInput.value.replace(/\D/g, "");
      if (digits.length < 6 || digits.length > 15) {
        showError(say("errPhone"));
        phoneInput.focus();
        return;
      }
    }

    hideError();
    submitBtn.disabled = true;
    setBtnText(say("submitting"));
    statusEl.textContent = say("statusSubmitting");

    /* The consent paragraph is sent with the signup and stored verbatim
       alongside it — for SMS/email marketing you need a record of the exact
       wording the person agreed to, in the language they saw. */
    const payload = mode === "email"
      ? { channel: "email", email: emailInput.value.trim() }
      : {
          channel: "sms",
          phone: phoneInput.value.replace(/\D/g, ""),
          dialCode: countryDial.textContent,
          country: COUNTRIES[focusIdx][1],
        };

    payload.locale = lang;
    payload.consentText = legalEl ? legalEl.textContent.trim() : "";
    payload.company = hpInput ? hpInput.value : "";   // honeypot; must be empty
    payload.elapsedMs = Date.now() - formShownAt;     // bots submit instantly

    subscribe(payload).then(ok => {
      if (!ok) {
        submitBtn.disabled = false;
        setBtnText(say("submit"));
        statusEl.textContent = "";
        showError(say("errNetwork"));
        return;
      }
      done = true;
      setBtnText(say("done"));
      statusEl.textContent = say("statusDone");
      submitBtn.classList.add("is-success");
      emailInput.disabled = true;
      phoneInput.disabled = true;
      pulse = 0.85;
    });
  });

  /* Posts to the endpoint that ships alongside the site, so there is no CORS
     and no key in the browser. A 409 means "already on the list", which is a
     success from the visitor's point of view — never tell someone their own
     address is a problem when it is simply already there. */
  async function subscribe(payload) {
    /* The published preview has no backend behind it. This flag is injected
       only by the artifact build so the form can be demonstrated; the real
       deployment never sets it, so a genuine outage still surfaces. */
    if (window.INFERMONDS_DEMO) {
      await new Promise(r => setTimeout(r, 1400));
      return true;
    }
    try {
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.status === 409) return true;
      return res.ok;
    } catch {
      return false;   // offline, blocked, endpoint down
    }
  }
})();
